import {
  SecondaryCategory,
  type SecondaryCategoryConstructor,
} from '@webapp/core/entities/internal/SecondaryCategory'
import {
  AccountCode,
  type AccountCodeConstructor,
} from '@webapp/platform/finance'
import { type BudgetIntervalTypeEnum } from '../enums/BudgetIntervalTypes'

export interface BudgetCategoryConstructorData {
  id: number
  uuid: uuid
  name: string
  amount: Nullable<string>
  interval: Nullable<BudgetIntervalTypeEnum>
  secondaryCategories: SecondaryCategoryConstructor[]
  accountCodes: AccountCodeConstructor[]
  startDate: Nullable<ISODateString> // In rare instances, budgets may not have start/end dates. For example, a user may import a budget without dates.
  endDate: Nullable<ISODateString>
  approvedSpend: string
  currentRequestPendingSpend: string // This is the pending amount for the current request
  invoicedSpend: string
  receivedSpend: string
  pendingSpend: string // This is the total pending amount associated with the budget (ie all requests matching the budget)
  purchasedSpend: string
  varianceAmount: Nullable<string>
  variancePercentage: Nullable<string>
}

export class BudgetCategory {
  id: number
  uuid: uuid
  name: string
  amount: Nullable<string>
  interval: Nullable<BudgetIntervalTypeEnum>
  secondaryCategories: SecondaryCategory[]
  accountCodes: AccountCode[]
  spend: {
    approvedSpend: string
    currentRequestPendingSpend: string
    invoicedSpend: string
    receivedSpend: string
    pendingSpend: string
    purchasedSpend: string
  }
  startDate: Nullable<Date>
  endDate: Nullable<Date>
  varianceAmount: Nullable<string>
  variancePercentage: Nullable<string>

  constructor(data: BudgetCategoryConstructorData) {
    this.id = data.id
    this.uuid = data.uuid
    this.name = data.name
    this.amount = data.amount
    this.interval = data.interval
    this.secondaryCategories = data.secondaryCategories.map(
      (secondaryCategory) => new SecondaryCategory(secondaryCategory)
    )
    this.accountCodes = data.accountCodes.map(
      (accountCode) => new AccountCode(accountCode)
    )
    this.spend = {
      approvedSpend: data.approvedSpend,
      currentRequestPendingSpend: data.currentRequestPendingSpend,
      invoicedSpend: data.invoicedSpend,
      receivedSpend: data.receivedSpend,
      pendingSpend: data.pendingSpend,
      purchasedSpend: data.purchasedSpend,
    }
    this.startDate = data.startDate ? new Date(data.startDate) : null
    this.endDate = data.endDate ? new Date(data.endDate) : null
    this.varianceAmount = data.varianceAmount
    this.variancePercentage = data.variancePercentage
  }
}
